import {
  isEnumValue,
  isResolvedResource,
} from "@fartherfinance/frontend/api/TaskWorkflows/utils/guards";

import type { EnumOption } from "../../core/types";

/**
 * Matches an initial value against available options using TaskWorkflows API type guards
 * @param value - The initial value to match
 * @param options - Available options to match against
 * @returns The matched option or undefined
 */
export function matchValueToOption(
  value: unknown,
  options: EnumOption[]
): EnumOption | undefined {
  if (!value || options.length === 0) {
    return undefined;
  }

  // Handle primitive values (string/number) - direct value match
  if (typeof value === "string" || typeof value === "number") {
    return options.find((opt) => opt.value === value);
  }

  if (typeof value === "object" && value !== null) {
    // Use API guard for EnumValue type (id/displayValue structure)
    if (isEnumValue(value)) {
      return options.find((opt) => opt.value === value.id);
    }

    // Use API guard for resolved resource check (type/value/kind structure)
    if (isResolvedResource(value)) {
      // First try direct value match
      const directMatch = options.find((opt) => opt.value === value.value);
      if (directMatch) {
        return directMatch;
      }

      // Then try type + value match for more specific matching
      return options.find(
        (opt) =>
          "type" in opt && opt.type === value.type && opt.value === value.value
      );
    }

    // Handle any other object with `id` property (backward compatibility)
    if (
      typeof value === "object" &&
      "id" in value &&
      typeof (value as any).id === "string"
    ) {
      return options.find((opt) => opt.value === (value as any).id);
    }

    // Handle any other object with `value` property (backward compatibility)
    if (typeof value === "object" && "value" in value) {
      return options.find((opt) => opt.value === (value as any).value);
    }
  }

  return undefined;
}
